"use client";

import Comments from "@/components/content/comments";
import PreviewModal from "@/components/content/previewModal";
import SerialEpisode from "@/components/series/serialEpisode";
import { ROUTES } from "@/routes/appRoutes";
import _ from "lodash";
import React, { useMemo } from "react";
import Link from "next/link";
import UserReport from "@/components/content/userReport";
import SerialContentActions from "@/components/content/serialActions";
import { getCMSImageURL } from "@/helpers/CMSImageURL";
import { Chip } from "@nextui-org/react";
import useWindowSize from "@/helpers/hooks/useWindowSize";
import ContentSuggestionsRail from "@/components/content/ContentSuggestionsRail";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import Cards from "@/components/content/cards";
import classNames from "classnames";
import { mergeArtistDuplicates } from "@/helpers/data.utils";
import DetailHeroScene from "@/components/common/DetailHeroScene";
import { getOptimizedImageForType } from "@/utils/cms.utils";
import { useContentViewAnalytics } from "@/hooks/useMediaAnalytics";

const tagsGuide = {
  "ترکی دیلینده": "TURKISH_NATIVE",
  "پخش زنده": "LIVE_STREAM",
  ["بزودی"]: "COMING_SOON",
  "آنلاین باخیش": "ONLINE_CINEMA",
  ["آرشیو"]: "ARCHIVE",
};

const SerialDetail = ({ data, isAdminPreview = false }) => {
  const windowSize = useWindowSize();
  useContentViewAnalytics(data, { enabled: !isAdminPreview });
  const { canPlay, canPlayLoading } = useSubscriptionStatus();

  const processedGenreInfo = useMemo(() => {
    return data?.genreInfo?.map((item) => ({ ...item, isGenre: true })) || [];
  }, [data?.genreInfo]);

  const genreAndCategory = useMemo(() => {
    return [...processedGenreInfo];
  }, [processedGenreInfo]);

  const { contentPhotos, artists } = data;

  const artistList = useMemo(() => {
    return (
      artists?.reduce((acc, i) => {
        const { farsiName, image, _id, slug } = i.artist?.meta?.bin || {};
        return acc.concat({
          artist: {
            farsiName,
            profession: i.profession?.name,
            contentPhotos: [{ type: "banner", src: image }],
            id: _id,
            slug,
            priority: i.priority ?? i.artist?.priority,
          },
        });
      }, []) || []
    );
  }, [artists]);

  const director = useMemo(() => {
    return artistList?.find((x) => x.artist.profession === "کارگردان")?.artist;
  }, [artistList]);

  const seasonDetails = data?.contentType?.seriesInfo;

  const lastInfo = useMemo(() => {
    try {
      const groupedByLodash = _.groupBy(seasonDetails, "seasonNumber");
      const seasons = Object.keys(groupedByLodash)?.map((key) => {
        return { [key]: groupedByLodash[key] };
      });

      const lastSeason = seasons[seasons?.length - 1]?.[seasons?.length];
      const lastEpisode = lastSeason.sort(
        (a, b) => b.episodeNumber - a.episodeNumber
      )?.[0];

      return {
        hasInfo: true,
        lastEpisode,
      };
    } catch (error) {
      return {
        hasInfo: false,
      };
    }
  }, [seasonDetails]);

  return (
    <div className="content-detail-page content-detail-series-page h-auto min-h-[100vh]">
      <div className="mb-10">
        <DetailHeroScene
          content={data}
          isSeries={true}
          renderButtons={() => (
            <>
              <PreviewModal src={data.trailer} />
              <SerialContentActions contentKey={data.slug} />
              <UserReport contentKey={data.slug} data={data} />
            </>
          )}
        />
      </div>
      <div className="pt-5 mx-auto w-full  md:container px-5 2xl:px-0 xlg:max-w-[1920px]">
        <SerialEpisode
          detail={seasonDetails}
          data={data}
          title={data.title}
          canPlay={canPlay}
          canPlayLoading={canPlayLoading}
        />
        {artistList?.length > 0 && (
          <section className="detail-artist-section lg:py-10 md:py-12 py-6 mb-5 relative z-10">
            <Cards
              items={mergeArtistDuplicates(artistList)}
              isArtist={true}
              isSwiper={true}
              noTag={true}
              categoryName="هنرمندان"
              showTitle={true}
              type="artist"
              noContainer={true}
            />
          </section>
        )}
        <ContentSuggestionsRail
          slug={data.slug}
          type="movie"
          contentKind={data?.contentType?.kind || "series"}
          enabled={!isAdminPreview}
          className="w-full md:container px-5 py-10 relative z-10"
        />
        <div className="py-6 md:py-10"></div>
        <section className="detail-comments-section">
          <Comments contentKey={data.slug} />
        </section>
      </div>
    </div>
  );
};

export default SerialDetail;
